import React from 'react';
import {
    Button, Collapse, FormGroup, Progress,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { isEqual, orderBy } from 'lodash-es';
import {
    faSave,
    faFileInvoiceDollar,
    faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { upsert } from '@s20/common/util/array';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import BillingRateForm from '../billing/BillingRateForm';
import BillingRateHistoryForm from '../billing/BillingRateHistoryForm';
import { BillingRateGrid } from '../billing/BillingRateGrid';
import CommonContext, {
    ApiRoutes, AppNavPaths, ChargeTypeResourceTypes, ChargeTypeUnits,
} from '../Common';
import {
    AppPageForm,
    FlexCenterRow,
    FlexRow,
    FormCheckbox,
    FormGroupColumn,
    FormLabel,
    GroupedRow,
    InfoCheckbox,
    Input,
    NumericInput,
    onFieldChange,
    onReactSelectChanged,
    SubHeading,
    Textarea,
    ToastMessage,
} from '../common/forms/FormElements';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { Block } from '../common/Block';
import { LongTermStatusType } from '../longTermStatus/LongTermStatus';
import { BillingRateTypes } from '../billing/BillingRate';
import { ChargeTypeConfigurationInfo } from './ChargeTypeConfigurationInfo';
import ChargeTypeConfigurationForm from './ChargeTypeConfigurationForm';
import {
    ChargeType,
    ChargeTypeConfiguration,
    ChargeTypeViewModel,
    ChargeTypeBillingRate,
    ChargeTypeUnitsName,
    NonUnionPayrollChargeTypes,
    PayrollChargeTypes,
} from './ChargeType';

// If breaks are turned off, we should clear whether or not they are paid,
// as well as clearing out the description.
const processCanBreak = (entity, checked) => {
    entity.canBreak = checked;
    entity.paidBreak = !checked ? false : entity.paidBreak;
    entity.breakDescription = !checked ? '' : entity.breakDescription;
    entity.breakHoursExceededThreshold = !checked
        ? 0
        : entity.breakHoursExceededThreshold;
    return entity;
};

class ChargeTypeForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.minPayRef = React.createRef();
        this.maxPayRef = React.createRef();
        this.exceptionFormRef = React.createRef();
        this.rateFormRef = React.createRef();
        this.rateHistoryFormRef = React.createRef();
        this.state = new ChargeTypeViewModel();
        this.handlers.change = this.handlers.change.bind(this);
        this.handlers.select = this.handlers.select.bind(this);
        this.handlers.baseConfig.canBreak = this.handlers.baseConfig.canBreak.bind(this);
        this.handlers.selectedException.canBreak = this.handlers.selectedException.canBreak.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps
            && this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }
    }

    onAddException = () => {
        this.exceptionFormRef.current.resetForm(); // clear validation
        this.setState((state) => {
            state.selectedException = {
                ...new ChargeTypeConfiguration(),
                chargeTypeId: state.chargeType.id ?? null,
                chargeTypeConfigurationTypeId: 2,
            };
            state.showExceptionForm = true;
            state.isNewException = true;
            return state;
        });
        this.context.setFormOpened(true); // lock all other page fields while form is open
    };

    onExceptionRemoved = () => {
        const { selectedException, chargeType } = this.state;

        chargeType.exceptions = chargeType.exceptions.filter(
            (x) => x.id !== selectedException.id,
        );
        this.exceptionFormRef.current.resetForm();
        this.setState({
            chargeType,
            showExceptionForm: false,
            selectedException: { ...new ChargeTypeConfiguration() },
        });
    };

    onAddRate = () => {
        const { chargeType } = this.state;
        const selectedRate = { ...new ChargeTypeBillingRate(), chargeTypeId: chargeType?.id };

        this.setState({
            selectedRate,
            showRateForm: true,
        });

        this.rateFormRef.current.resetForm(); // clear validation
        this.context.setFormOpened(true); // lock all other page fields while form is open
    };

    onClearErrors = () => this.setState({ errors: {} });

    onEditException = (exception) => {
        const cloned = clone(exception);
        this.exceptionFormRef.current.resetForm();
        // setting null for the existing payunits
        cloned.minimumPayUnits = null;
        this.setState({ selectedException: cloned, showExceptionForm: true, isNewException: false });
        this.context.setFormOpened(true);
    };

    onEditRate = (rate) => {
        const cloned = clone(rate);
        this.rateFormRef.current.resetForm();

        this.setState({
            selectedRate: {
                ...cloned,
                rateType: BillingRateTypes.CHARGE_TYPE,
                id: cloned.billingRateId,
            },
            showRateForm: true,
            showRateFormDelete: true,
        });
        this.context.setFormOpened(true);
    };

    onExceptionFormClosed = () => this.setState({
        showExceptionForm: false,
        isNewException: true,
        selectedException: { ...new ChargeTypeConfiguration() },
    });

    onExceptionSaved = () => {
        const { selectedException, chargeType } = this.state;

        if (chargeType.unitsId === ChargeTypeUnits.Flat) {
            selectedException.paidBreak = null;
            selectedException.canBreak = null;
            selectedException.breakDescription = null;
            selectedException.minimumPayUnits = null;
        }

        if (selectedException.doesNotApply) {
            selectedException.increment = null;
            selectedException.defaultValue = null;
            selectedException.paidBreak = null;
            selectedException.canBreak = null;
            selectedException.breakDescription = null;
            selectedException.minimumPayUnits = null;
        }

        if (!selectedException.breakHoursExceededThreshold) {
            selectedException.breakHoursExceededThreshold = 0;
        }

        chargeType.exceptions = upsert(
            selectedException,
            chargeType.exceptions,
            'id',
        );
        this.exceptionFormRef.current.resetForm();
        this.setState({
            chargeType,
            showExceptionForm: false,
        });
    };

    onRateFormClosed = () => {
        this.rateFormRef.current.resetForm(); // clear validation
        this.context.setFormOpened(false); // lock all other page fields while form is open
        this.setState({
            showRateForm: false,
            showRateFormDelete: false,
        });
    };

    onRateHistoryFormClosed = () => {
        this.rateFormRef.current.resetForm(); // clear validation
        this.context.setFormOpened(false); // lock all other page fields while form is open
        this.setState({
            showRateHistoryForm: false,
        });
    };

    onRateRemoved = () => {
        const { selectedRate, chargeType } = this.state;

        chargeType.billingRates = chargeType.billingRates.filter(
            (x) => x.billingRateId !== selectedRate.billingRateId,
        );
        this.rateFormRef.current.resetForm();
        this.setState({
            chargeType,
            showRateForm: false,
        });
    };

    onRateSaved = () => {
        const { selectedRate, chargeType } = this.state;

        const rate = chargeType.billingRates.find(
            (x) => !x.billingRate.expirationDate
                || x.billingRate.expirationDate
                >= selectedRate.billingRate.effectiveDate,
        );
        if (rate) {
            // set the expiration date
            const effective = new Date(
                clone(selectedRate.billingRate.effectiveDate),
            );

            // see if any have same effective date
            if (typeof selectedRate.billingRateId === 'string') {
                if (
                    new Date(rate.billingRate.effectiveDate).getDate()
                    == effective.getDate()
                    && rate.billingRateId !== selectedRate.billingRateId
                ) {
                    // pop notification?
                    this.rateFormRef.current.resetForm();
                    this.setState({
                        showRateForm: false,
                    });
                    return;
                }
                // set it back one day.
                effective.setDate(effective.getDate() - 1);
                rate.billingRate.expirationDate = effective;
            }
        }

        chargeType.billingRates = upsert(
            selectedRate,
            chargeType.billingRates ?? [],
            'billingRateId',
        );
        this.rateFormRef.current.resetForm();
        this.setState({
            chargeType,
            showRateForm: false,
        });
    };

    onShowRateHistory = async (rate) => {
        //
        const history = await httpClient.js(
            ApiRoutes.billingRate.chargeTypeBillingRateHistory(
                rate.chargeTypeId,
            ),
        );

        this.rateHistoryFormRef.current.resetForm();
        this.setState({
            selectedRateHistory: history,
            showRateHistoryForm: true,
        });
        this.context.setFormOpened(true); // lock all other page fields while form is open
    };

    onSubmit = async () => {
        this.onClearErrors();
        this.setSaving(true);

        if (this.state.chargeType.baseConfig.isPermit && !this.state.chargeType.baseConfig.isSurcharge) {
            toast.error('Permit Fees must be configured as a surcharge.');
            this.setSaving(false);
            return;
        }

        // if (!this.additionalValidation()) {
        //    this.setSaving(false);
        //    return false;
        // }

        const cloned = clone(this.state.chargeType);

        /** Revert temporary hashes used in row keys.  Need this to get around
            fluent API.
        */
        if (typeof cloned.baseConfig.id === 'string') cloned.baseConfig.id = null;

        for (const exception of cloned.exceptions) {
            exception.id = typeof exception.id === 'string' ? null : exception.id;
        }

        for (const rate of cloned.billingRates) {
            rate.billingRateId = typeof rate.billingRateId === 'string'
                ? null
                : rate.billingRateId;
            rate.billingRate.id = typeof rate.billingRate.id === 'string'
                ? null
                : rate.billingRate.id;
            rate.chargeTypeId = typeof rate.chargeTypeId === 'string'
                ? null
                : rate.chargeTypeId;
        }

        if (this.props.location.pathname == AppNavPaths.ChargeTypeNew) {
            await this.create(cloned);
        } else {
            await this.update(cloned);
        }
    };

    setSaving = (b) => this.setState({ saving: b });

    create = async (chargeType) => {
        chargeType.id = null;

        const response = await httpClient
            .post(ApiRoutes.chargeType.create(), chargeType)
            .catch(this.handleSaveError);

        if (response && !isNaN(response)) {
            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message="Saved."
                />,
            );

            this.context.history.push(
                `${AppNavPaths.ChargeType}/${response}`,
            );
        }

        this.setSaving(false);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    handlers = {
        baseConfig: {
            canBreak: (e) => {
                const { checked } = e.target;
                this.setState(
                    (state) => (
                        (state.chargeType.baseConfig = processCanBreak(
                            state.chargeType.baseConfig,
                            checked,
                        )),
                        state
                    ),
                );
            },
        },
        selectedException: {
            canBreak: (e) => {
                const { checked } = e.target;
                this.setState(
                    (state) => (
                        (state.selectedException = processCanBreak(
                            state.selectedException,
                            checked,
                        )),
                        state
                    ),
                );
            },
            DNA: (e) => {
                const { checked } = e.target;
                const selectedException = { ...this.state.selectedException };
                if (!checked) {
                    selectedException.increment = '';
                    selectedException.canBreak = false;
                    selectedException.paidBreak = false;
                    selectedException.breakDescription = '';
                    selectedException.minimumPayUnits = '';
                }
                selectedException.doesNotApply = checked;
                this.setState({ selectedException });
            },
        },
        change: onFieldChange,
        select: onReactSelectChanged,
        onChargeUnitsChanged: (s) => {
            // baseconfig only
            const { chargeType } = this.state;
            const val = (s ?? {}).value;
            chargeType.unitsId = val;
            // if flat clear out non flat fields and set to 1
            if (val && val == 2) {
                chargeType.baseConfig.minimumPayUnits = '';
                chargeType.baseConfig.breakDescription = '';
                chargeType.baseConfig.canBreak = false;
                chargeType.baseConfig.paidBreak = false;
                chargeType.isPayable = false;
            } else if (val && val == 3) {
                chargeType.resourceTypeId = 3;
                chargeType.baseConfig.isSurcharge = true;
                chargeType.isPayable = false;
            } else {
                chargeType.isPayable = chargeType.resourceTypeId == ChargeTypeResourceTypes.Employee;
            }

            chargeType.baseConfig.increment = '';
            chargeType.baseConfig.defaultValue = '';

            this.setState({
                chargeType,
            });
        },
        onChargeResourceTypeChanged: (s) => {
            const { chargeType } = this.state;
            const val = (s ?? {}).value;
            chargeType.resourceTypeId = val;
            //If Rental Set Charge Type Unit to Flat
            if (val && val == ChargeTypeResourceTypes.Rental) {
                chargeType.unitsId = ChargeTypeUnits.Flat;
            }
            //If Not Employee Set Charge Type isPayable to false
            if (!val || val != ChargeTypeResourceTypes.Employee) {
                chargeType.isPayable = false;
            } else {
                chargeType.isPayable = chargeType.unitsId == ChargeTypeUnits.Hours;
            }

            if (val === ChargeTypeResourceTypes.Sale) {
                chargeType.unitsId = ChargeTypeUnits.Flat;
                chargeType.applicabilityId = 3;
                chargeType.isBillable = true;
            }

            this.setState({
                chargeType,
            });
        },
        onIsCreditChanged: (e) => {
            const isCredit = e.target.checked;

            this.setState((prev) => {
                const chargeType = { ...prev.chargeType, isCredit };

                if (isCredit) {
                    chargeType.resourceTypeId = ChargeTypeResourceTypes.None;
                    chargeType.isPayable = false;
                    chargeType.isBillable = true;
                    chargeType.unitsId = ChargeTypeUnits.Flat;
                    chargeType.applicabilityId = 3;
                    chargeType.payrollChargeTypeId = PayrollChargeTypes.None;
                }

                return { chargeType };
            });
        },
    };

    hasAFADEquipment = () => {
        const { chargeType, equipmentTypes } = this.state;

        if (!chargeType?.equipment || chargeType.equipment.length < 1 || chargeType.resourceTypeId !== ChargeTypeResourceTypes.Equipment) {
            return false;
        }

        let i = 0;
        const len = chargeType.equipment.length;

        for (; i < len; i += 1) {
            const equipmentTypeId = chargeType.equipment[i];
            const equipment = equipmentTypes.find((type) => type.value === equipmentTypeId);

            if (!equipment?.isAFAD) {
                return false;
            }
        }

        return true;
    };

    hasSpecificEquipmentOnly = () => {
        const { chargeType, equipmentTypes } = this.state;

        if (!chargeType?.equipment || chargeType.equipment.length < 1 || chargeType.resourceTypeId !== ChargeTypeResourceTypes.Rental) {
            return false;
        }

        let i = 0;
        const len = chargeType.equipment.length;

        for (; i < len; i += 1) {
            const equipmentTypeId = chargeType.equipment[i];
            const equipment = equipmentTypes.find((type) => type.value === equipmentTypeId);

            if (equipment?.isGeneric) {
                return false;
            }
        }

        return true;
    };

    hasHourlyEquipment = () => {
        const { chargeType } = this.state;

        if (chargeType.resourceTypeId === ChargeTypeResourceTypes.Equipment && chargeType.unitsId === ChargeTypeUnits.Hours) {
            return true;
        }

        return false;
    };

    async populateState() {
        const { id } = this.props.match.params;

        const [
            surcharges,
            resourceTypes,
            resGroups,
            groups,
            units,
            statuses,
            equipmentTypes,
            customers,
            chargeType,
            chargeTypeApplicability,
            payrollChargeTypes,
            paySources,
            equipmentLongTermStatuses,
            chargeTypeTrackingTypes,
        ] = await Promise.all([
            httpClient.js(ApiRoutes.chargeType.getSurcharges()),
            httpClient.js(ApiRoutes.typeAheads.jobResourceTypes()),
            httpClient.js(ApiRoutes.userGroup.all()),
            httpClient.js(ApiRoutes.auth.groups()),
            httpClient.js(ApiRoutes.chargeTypeUnit.all()),
            httpClient.js(ApiRoutes.chargeTypeStatus.all()),
            httpClient.js(ApiRoutes.equipmentType.all()),
            httpClient.js(ApiRoutes.typeAheads.customers()),
            id
                ? httpClient.js(ApiRoutes.chargeType.byId(id))
                : new ChargeType(),
            httpClient.js(ApiRoutes.typeAheads.chargeTypeApplicability()),
            httpClient.js(ApiRoutes.payrollChargeType.all()),
            httpClient.js(ApiRoutes.typeAheads.getChargeTypePaySources()),
            httpClient.js(ApiRoutes.longTermStatus.byTypeId(LongTermStatusType.Equipment)),
            httpClient.js(ApiRoutes.typeAheads.chargeTypeTrackingTypes()),
        ]);
        const originalData = clone(chargeType);
        this.setState({
            originalData,
            chargeType,
            resourceTypes,
            customers,
            chargeTypeApplicability,
            loading: false,
            surcharges: surcharges.filter((x) => x.resourceTypeId !== ChargeTypeResourceTypes.Setup),
            setupSurcharges: surcharges.filter((x) => x.resourceTypeId === ChargeTypeResourceTypes.Setup),
            resGroups,
            groups: groups.map((x) => ({ label: x.groupName, value: x.id })),
            units,
            equipmentTypes: (equipmentTypes ?? []).map((x) => ({
                label: x.description,
                value: x.id,
                isAFAD: x.equipmentTypeAFADType.isAFAD,
                isGeneric: x.isGeneric,
            })),
            statuses,
            saving: false,
            paySources,
            chargeTypeTrackingTypes,
            equipmentLongTermStatuses,
            payrollChargeTypes: [{ label: 'None', value: -1 }, ...payrollChargeTypes],
        });
    }

    update = async (chargeType) => {
        const response = await httpClient
            .put(ApiRoutes.chargeType.byId(chargeType.id), chargeType)
            .catch(this.handleSaveError);

        if (response && parseInt(response.id ?? 0) >= 0) {
            this.setState({
                chargeType: response,
                originalData: response /* also restore this to reset Prompt */,
                saving: false,
                formValidated: false,
            });

            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message="Charge Type saved."
                />,
            );
        }

        this.setSaving(false);
    };

    renderForm() {
        // reducers
        const {
            billableOptions,
            chargeType,
            increments,
            units,
            resourceTypes,
            resGroups,
            surcharges,
            setupSurcharges,
            groups,
            statuses,
            equipmentTypes,
            customers,
            chargeTypeApplicability,
            originalData,
            saving,
            showRateFormDelete,
            isNewException,
            payrollChargeTypes,
            paySources,
            equipmentLongTermStatuses,
            chargeTypeTrackingTypes,
        } = this.state;
        const newChargeType = parseInt(chargeType.id ?? 0) <= 0;
        if (!chargeType) return '';

        const { baseConfig } = chargeType;
        const unitsName = ChargeTypeUnitsName[chargeType.unitsId] || '';

        const { tenantSettings } = this.context.tenant;

        const {
            billingEnabled,
            billingRatesEnabled,
            useLimitedApprovals,
            allowDefaultChargeTypeValues,
            enableAFADChargeTypeGrouping,
        } = tenantSettings;

        const hasHourlyEquipment = this.hasHourlyEquipment();
        const hasSpecificEquipmentOnly = this.hasSpecificEquipmentOnly();

        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, chargeType)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    backUrl={AppNavPaths.ChargeTypes}
                    backLabel="Back to Charge Types"
                    formShown={this.context.formIsOpen}
                    formId="chargeTypeForm"
                    formHeadingIcon={faFileInvoiceDollar}
                    formHeading={
                        newChargeType ? 'New Charge Type' : 'Edit Charge Type'
                    }
                    formName="chargeTypeForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={this.state.formValidated}
                    saving={this.state.saving}
                    errors={this.state.errors}
                    loading={this.state.loading}
                >
                    <GroupedRow>
                        <FormGroupColumn>
                            <Input
                                label="Charge Type Description"
                                id="description"
                                name="chargeType.description"
                                value={chargeType.description ?? ''}
                                onChange={this.handlers.change}
                                placeholder="Enter description"
                                type="text"
                                required
                                error="Charge Type Description is required."
                            />
                            <FormGroup>
                                <FormCheckbox
                                    id="chargeType.isCredit"
                                    name="chargeType.isCredit"
                                    checked={
                                        chargeType.isCredit ?? ''
                                    }
                                    onChange={this.handlers.onIsCreditChanged}
                                    labelText="Is Credit"
                                />
                            </FormGroup>
                            {chargeType.resourceTypeId !== ChargeTypeResourceTypes.Sale && (
                                <ValidatedSelect
                                    id="units"
                                    name="chargeType.unitsId"
                                    label="Charge Units"
                                    required
                                    isDisabled={
                                        (chargeType.exceptions ?? []).length > 0
                                        || chargeType.resourceTypeId === ChargeTypeResourceTypes.Rental
                                        || chargeType.isCredit
                                    }
                                    options={units}
                                    value={
                                        (units ?? []).find(
                                            (s) => s.value === chargeType.unitsId,
                                        ) ?? ''
                                    }
                                    onChange={this.handlers.onChargeUnitsChanged.bind(
                                        this,
                                    )}
                                    error="A units selection is required."
                                />
                            )}

                            <ValidatedSelect
                                id="isBillable"
                                name="chargeType.isBillable"
                                label="Billable"
                                required
                                options={billableOptions}
                                value={
                                    billableOptions.find(
                                        (bo) => bo.value
                                            === chargeType.isBillable,
                                    ) ?? ''
                                }
                                onChange={this.handlers.select}
                                isDisabled={chargeType.isCredit}
                                error="Choose whether or not this charge type is billable."
                            />

                            {chargeType.unitsId === ChargeTypeUnits.Hours && chargeType.resourceTypeId === ChargeTypeResourceTypes.Employee && (
                                <>
                                    <ValidatedSelect
                                        id="isPayable"
                                        label="Payable"
                                        name="chargeType.isPayable"
                                        required
                                        options={billableOptions}
                                        value={
                                            billableOptions.find(
                                                (bo) => bo.value
                                                    === chargeType.isPayable,
                                            ) ?? ''
                                        }
                                        onChange={(opt, meta) => {
                                            this.handlers.select(opt, meta);
                                            if (opt?.value !== true) {
                                                this.setState((prevState) => ({
                                                    chargeType: {
                                                        ...prevState.chargeType,
                                                        payrollChargeTypeId: PayrollChargeTypes.None,
                                                    },
                                                }));
                                            }
                                        }}
                                        isDisabled={chargeType.isCredit}
                                        error="Choose whether or not this charge type is payable."
                                    />

                                    {tenantSettings.enforceContractUnionWork !== true && chargeType.isPayable === true && (
                                        <ValidatedSelect
                                            id="chargeType.payrollChargeTypeId"
                                            name="chargeType.payrollChargeTypeId"
                                            label="Payroll Charge Category"
                                            options={payrollChargeTypes?.filter((x) => NonUnionPayrollChargeTypes.includes(x.value))}
                                            value={
                                                (payrollChargeTypes ?? []).find(
                                                    (x) => x.value === (chargeType.payrollChargeTypeId ?? -1),
                                                ) ?? ''
                                            }
                                            onChange={this.handlers.select}
                                            isClearable
                                            error="A category must be selected"
                                        />
                                    )}
                                </>
                            )}

                            <Textarea
                                label="Info Description"
                                id="information"
                                maxLength={1000}
                                name="chargeType.information"
                                value={chargeType.information ?? ''}
                                onChange={this.handlers.change}
                                placeholder="Enter text"
                                rows="5"
                                required
                                error="Info Description is required."
                            />

                            <ValidatedSelect
                                id="status"
                                name="chargeType.statusId"
                                label="Status"
                                required
                                options={statuses}
                                value={
                                    (statuses ?? []).find(
                                        (x) => x.value === chargeType.statusId,
                                    ) ?? ''
                                }
                                onChange={this.handlers.select}
                                error="At least one equipment type is required"
                            />

                            {chargeType.resourceTypeId !== ChargeTypeResourceTypes.Sale && (
                                <ValidatedSelect
                                    id="applicability"
                                    name="chargeType.applicabilityId"
                                    label="Charge Usage"
                                    options={chargeTypeApplicability}
                                    value={
                                        (chargeTypeApplicability ?? []).find(
                                            (x) => x.value
                                                === chargeType.applicabilityId,
                                        ) ?? ''
                                    }
                                    isDisabled={chargeType.isCredit}
                                    onChange={this.handlers.select}
                                />
                            )}

                        </FormGroupColumn>
                        <FormGroupColumn>
                            <ValidatedSelect
                                id="resType"
                                name="chargeType.resourceTypeId"
                                label="Resource Type"
                                required
                                options={resourceTypes}
                                isDisabled={!newChargeType || chargeType.unitsId === ChargeTypeUnits.Percentage || chargeType.isCredit}
                                value={
                                    (resourceTypes ?? []).find(
                                        (x) => x.value
                                            === chargeType.resourceTypeId,
                                    ) ?? ''
                                }
                                onChange={this.handlers.onChargeResourceTypeChanged.bind(
                                    this,
                                )}
                                error="A type is required"
                            />

                            {chargeType.resourceTypeId !== null && <hr />}

                            {chargeType.resourceTypeId === ChargeTypeResourceTypes.Setup && (
                                <>
                                    <FormGroup>
                                        <FormLabel label="Resource Type Options" />

                                        <FormCheckbox
                                            id="chargeType.baseConfig.isPermit"
                                            name="chargeType.baseConfig.isPermit"
                                            checked={
                                                baseConfig.isPermit ?? ''
                                            }
                                            onChange={this.handlers.change}
                                            labelText="Is Permit Fee"
                                        />
                                    </FormGroup>

                                    {!baseConfig.isPermit && !baseConfig.isSurcharge
                                        && (
                                            <ValidatedSelect
                                                label="Setup Surcharge"
                                                id="chargeType.baseConfig.childChargeTypeId"
                                                name="chargeType.baseConfig.childChargeTypeId"
                                                isClearable
                                                options={setupSurcharges}
                                                value={
                                                    (setupSurcharges ?? []).find(
                                                        (x) => x.value
                                                            === baseConfig.childChargeTypeId,
                                                    ) ?? ''
                                                }
                                                onChange={this.handlers.select}
                                            />
                                        )}
                                </>
                            )}

                            {chargeType.resourceTypeId === ChargeTypeResourceTypes.Employee && (
                                <ValidatedSelect
                                    id="resGroups"
                                    label="Eligible Resource Group"
                                    name="chargeType.resourceGroupId"
                                    isClearable
                                    isDisabled={
                                        (chargeType.groups ?? []).length > 0
                                    }
                                    required={
                                        (chargeType.groups ?? []).length
                                        === 0
                                    }
                                    options={resGroups}
                                    value={
                                        (resGroups ?? []).find(
                                            (x) => x.value
                                                === chargeType.resourceGroupId,
                                        ) ?? ''
                                    }
                                    onChange={this.handlers.select}
                                    error="A group selection is required"
                                />
                            )}

                            {(chargeType.resourceTypeId === ChargeTypeResourceTypes.Equipment
                                || chargeType.resourceTypeId === ChargeTypeResourceTypes.Rental
                                || chargeType.resourceTypeId === ChargeTypeResourceTypes.Sale) && (
                                    <ValidatedSelect
                                        id="equipment"
                                        name="chargeType.equipment"
                                        label="Eligible Equipment Type(s)"
                                        isMulti
                                        required={chargeType.resourceTypeId !== ChargeTypeResourceTypes.Sale}
                                        options={equipmentTypes}
                                        value={
                                            (equipmentTypes ?? []).filter((x) => (
                                                chargeType.equipment ?? []
                                            ).includes(x.value)) ?? ''
                                        }
                                        onChange={this.handlers.select}
                                        error="At least one equipment type is required"
                                    />
                                )}

                            {enableAFADChargeTypeGrouping === true && this.hasAFADEquipment() && chargeType.resourceTypeId === ChargeTypeResourceTypes.Equipment && (
                                <NumericInput
                                    formGroupClassName="ml-3 w-auto"
                                    label="# of AFADs in a set"
                                    id="chargeType.baseConfig.numAFADsInSet"
                                    name="chargeType.baseConfig.numAFADsInSet"
                                    value={baseConfig.numAFADsInSet ?? 1}
                                    onChange={this.handlers.change}
                                    type="number"
                                    min={1}
                                    required={chargeType.resourceTypeId == 2}
                                    error="A value of at least 1 must be entered."
                                />
                            )}
                        </FormGroupColumn>
                    </GroupedRow>

                    {chargeType.resourceTypeId === ChargeTypeResourceTypes.Employee && useLimitedApprovals && (
                        <>
                            <SubHeading>Timesheet Security Groups</SubHeading>
                            <GroupedRow>
                                <FormGroupColumn>
                                    <ValidatedSelect
                                        id="approverGroups"
                                        label="Approver Group(s)"
                                        name="chargeType.approveLimitedApprovalGroups"
                                        isMulti
                                        options={groups}
                                        value={
                                            (groups ?? []).filter((x) => (
                                                chargeType.approveLimitedApprovalGroups
                                                ?? []
                                            ).includes(x.value)) ?? ''
                                        }
                                        onChange={this.handlers.select}
                                    />

                                    <ValidatedSelect
                                        id="rejectionGroups"
                                        name="chargeType.rejectionLimitedApprovalGroups"
                                        label="Rejection Group(s)"
                                        isMulti
                                        options={groups}
                                        value={
                                            (groups ?? []).filter((x) => (
                                                chargeType.rejectionLimitedApprovalGroups
                                                ?? []
                                            ).includes(x.value)) ?? ''
                                        }
                                        onChange={this.handlers.select}
                                    />

                                    <ValidatedSelect
                                        id="rejectionGroups"
                                        name="chargeType.editorLimitedApprovalGroups"
                                        label="Editor Group(s)"
                                        isMulti
                                        options={groups}
                                        value={
                                            (groups ?? []).filter((x) => (
                                                chargeType.editorLimitedApprovalGroups
                                                ?? []
                                            ).includes(x.value)) ?? ''
                                        }
                                        onChange={this.handlers.select}
                                    />
                                </FormGroupColumn>
                            </GroupedRow>
                        </>
                    )}

                    <SubHeading>Configuration</SubHeading>
                    <GroupedRow>
                        {(chargeType.unitsId === ChargeTypeUnits.Hours || chargeType.unitsId === ChargeTypeUnits.Percentage) && (
                            <>
                                <FormGroupColumn>
                                    <FormGroup>
                                        <FormLabel label="Surcharge Options" />
                                        <FlexRow className="ml-3">
                                            <FormCheckbox
                                                id="chargeType.baseConfig.isSurcharge"
                                                name="chargeType.baseConfig.isSurcharge"
                                                checked={
                                                    baseConfig.isSurcharge ?? ''
                                                }
                                                disabled={
                                                    chargeType.unitsId === ChargeTypeUnits.Percentage
                                                }
                                                onChange={this.handlers.change}
                                                labelText="Surcharge Charge Type"
                                            />
                                        </FlexRow>
                                    </FormGroup>
                                    {baseConfig.isSurcharge && (
                                        <>
                                            <NumericInput
                                                formGroupClassName="ml-3 w-auto"
                                                label="Bill Value"
                                                id="chargeType.baseConfig.surchargeBillAmount"
                                                name="chargeType.baseConfig.surchargeBillAmount"
                                                value={baseConfig.surchargeBillAmount ?? ''}
                                                onChange={this.handlers.change}
                                                required={!!baseConfig.surchargeBillAmount >= 0}
                                                error="Bill Value is required."
                                                endAddon={chargeType.unitsId === ChargeTypeUnits.Percentage && (
                                                    <span className="text-muted">%</span>
                                                )}
                                            />
                                            {chargeType.unitsId != ChargeTypeUnits.Percentage && (
                                                <NumericInput
                                                    formGroupClassName="ml-3 w-auto"
                                                    label="Pay Value"
                                                    id="chargeType.baseConfig.surchargePayAmount"
                                                    name="chargeType.baseConfig.surchargePayAmount"
                                                    value={baseConfig.surchargePayAmount ?? ''}
                                                    onChange={this.handlers.change}
                                                    required={!!baseConfig.surchargePayAmount >= 0}
                                                    error="Pay Value is required."
                                                />
                                            )}
                                            <Collapse
                                                isOpen={
                                                    chargeType.unitsId === ChargeTypeUnits.Hours
                                                    && chargeType.resourceTypeId === ChargeTypeResourceTypes.Employee
                                                }
                                            >
                                                <FormGroup className="ml-3">
                                                    <FormCheckbox
                                                        id="chargeType.baseConfig.surchargeAffectsDailyOT"
                                                        name="chargeType.baseConfig.surchargeAffectsDailyOT"
                                                        checked={
                                                            baseConfig.surchargeAffectsDailyOT ?? ''
                                                        }
                                                        onChange={
                                                            this.handlers.change
                                                        }
                                                        labelText="Surcharge affects daily/weekly OT"
                                                    />
                                                </FormGroup>
                                            </Collapse>
                                            {tenantSettings.autoCalculateDriveTimeBilling && (
                                                <FormGroup className="ml-3">
                                                    <FormCheckbox
                                                        id="chargeType.baseConfig.autoCalculatedDriveTime"
                                                        name="chargeType.baseConfig.autoCalculatedDriveTime"
                                                        checked={
                                                            baseConfig.autoCalculatedDriveTime ?? false
                                                        }
                                                        onChange={this.handlers.change}
                                                        labelText="Auto Calculated Drive Time"
                                                    />
                                                </FormGroup>
                                            )}
                                        </>
                                    )}
                                    {!baseConfig.isSurcharge && (
                                        <>
                                            <ValidatedSelect
                                                name="chargeType.baseConfig.increment"
                                                label="Increment"
                                                required
                                                options={increments}
                                                value={
                                                    (increments ?? []).find(
                                                        (i) => parseFloat(
                                                            i.value,
                                                        )
                                                            === parseFloat(
                                                                baseConfig.increment
                                                                ?? '0',
                                                            ),
                                                    ) ?? ''
                                                }
                                                onChange={
                                                    this.handlers.select
                                                }
                                                error="Increment is required."
                                            />

                                            <NumericInput
                                                label="Hours Warning Threshold"
                                                id="chargeType.baseConfig.hoursExceededThreshold"
                                                name="chargeType.baseConfig.hoursExceededThreshold"
                                                value={baseConfig.hoursExceededThreshold ?? ''}
                                                onChange={this.handlers.change}
                                                placeholder="Hours Warning Threshold"
                                            />
                                            <FormGroup>
                                                <FormLabel label="Break Options" />

                                                <InfoCheckbox
                                                    label="Enable Breaks"
                                                    name="chargeType.baseConfig.canBreak"
                                                    checked={baseConfig.canBreak}
                                                    onChange={this.handlers.baseConfig.canBreak}
                                                >
                                                    <InfoCheckbox
                                                        name="chargeType.baseConfig.paidBreak"
                                                        checked={baseConfig.paidBreak}
                                                        onChange={this.handlers.change}
                                                        label="Paid Breaks"
                                                        color="dark"
                                                        tooltip="Selecting this option will cause breaks to not be deducted from related charges."
                                                    />

                                                    <Input
                                                        label="Break Description"
                                                        name="chargeType.baseConfig.breakDescription"
                                                        value={baseConfig.breakDescription ?? ''}
                                                        onChange={this.handlers.change}
                                                        placeholder="Break description"
                                                        required={!!baseConfig.canBreak}
                                                        error="Break Description is required."
                                                    />

                                                    <NumericInput
                                                        label="Break Warning Threshold"
                                                        id="chargeType.baseConfig.breakHoursExceededThreshold"
                                                        name="chargeType.baseConfig.breakHoursExceededThreshold"
                                                        value={baseConfig.breakHoursExceededThreshold ?? ''}
                                                        onChange={this.handlers.change}
                                                        placeholder="Break Warning Threshold"
                                                    />
                                                </InfoCheckbox>
                                            </FormGroup>
                                        </>
                                    )}

                                    {chargeType.unitsId != ChargeTypeUnits.Percentage && (
                                        <>
                                            <FormGroup>
                                                <FormLabel label="Cancellation Options" />
                                                {(tenantSettings ?? {})
                                                    .blockBillingCancelEnabled && (
                                                        <FormGroup>
                                                            <FlexRow className="ml-3">
                                                                <FormCheckbox
                                                                    id="chargeType.baseConfig.blockBillingCancel"
                                                                    name="chargeType.baseConfig.blockBillingCancel"
                                                                    checked={
                                                                        baseConfig.blockBillingCancel
                                                                        ?? ''
                                                                    }
                                                                    onChange={
                                                                        this.handlers.change
                                                                    }
                                                                    labelText="Block Cancellation Min Bill Qty if Charge > 0 for Billing"
                                                                />
                                                            </FlexRow>
                                                        </FormGroup>
                                                    )}
                                                {(tenantSettings ?? {})
                                                    .blockPayrollCancelEnabled && (
                                                        <FormGroup>
                                                            <FlexRow className="ml-3">
                                                                <FormCheckbox
                                                                    id="chargeType.baseConfig.blockPayrollCancel"
                                                                    name="chargeType.baseConfig.blockPayrollCancel"
                                                                    checked={
                                                                        baseConfig.blockPayrollCancel
                                                                        ?? ''
                                                                    }
                                                                    onChange={
                                                                        this.handlers.change
                                                                    }
                                                                    labelText="Block Cancellation Min Pay Qty if Charge > 0 for Payroll"
                                                                />
                                                            </FlexRow>
                                                        </FormGroup>
                                                    )}
                                            </FormGroup>
                                            <NumericInput
                                                formGroupClassName="ml-3"
                                                label="Cancellation Time Threshold (Min)"
                                                id="chargeType.baseConfig.cancellationTimeThreshold"
                                                name="chargeType.baseConfig.cancellationTimeThreshold"
                                                value={baseConfig.cancellationTimeThreshold ?? ''}
                                                onChange={this.handlers.change}
                                                placeholder="Enter in Minutes"
                                                required={!!baseConfig.canBreak}
                                                error="Cancellation Time Threshold (Min) is required"
                                            />
                                            <NumericInput
                                                formGroupClassName="ml-3"
                                                label="Minimum Cancellation Bill Qty (Hours)"
                                                id="chargeType.baseConfig.cancellationBillQty"
                                                name="chargeType.baseConfig.cancellationBillQty"
                                                value={baseConfig.cancellationBillQty ?? ''}
                                                onChange={this.handlers.change}
                                                placeholder="Enter Qty in Hours"
                                                required={!!baseConfig.canBreak}
                                                step=".025"
                                                error="Minimum Cancellation Bill Qty (Hours) is required"
                                            />
                                        </>
                                    )}

                                </FormGroupColumn>

                                {!baseConfig.isSurcharge ? (
                                    <FormGroupColumn>
                                        <NumericInput
                                            label="Minimum Pay"
                                            ref={this.minPayRef}
                                            id="chargeType.baseConfig.minimumPayUnits"
                                            name="chargeType.baseConfig.minimumPayUnits"
                                            value={baseConfig.minimumPayUnits ?? ''}
                                            onChange={this.handlers.change}
                                            placeholder={`Enter  ${unitsName}`}
                                            step="0.25"
                                            required={!!baseConfig.minimumPayUnits}
                                        />
                                        {chargeType.isBillable && (
                                            <FormGroup>
                                                <FormCheckbox
                                                    id="chargeType.baseConfig.doNotApplyMinimumBill"
                                                    name="chargeType.baseConfig.doNotApplyMinimumBill"
                                                    checked={
                                                        baseConfig.doNotApplyMinimumBill
                                                        ?? ''
                                                    }
                                                    onChange={
                                                        this.handlers.change
                                                    }
                                                    labelText="Do Not Apply Minimum Bill"
                                                />
                                            </FormGroup>
                                        )}
                                        {(!chargeType.baseConfig.doNotApplyMinimumBill || !chargeType.isBillable) && (
                                            <NumericInput
                                                label="Minimum Bill"
                                                ref={this.minPayRef}
                                                id="chargeType.baseConfig.minimumBillUnits"
                                                name="chargeType.baseConfig.minimumBillUnits"
                                                value={baseConfig.minimumBillUnits ?? ''}
                                                onChange={this.handlers.change}
                                                placeholder={`Enter  ${unitsName}`}
                                                step="0.25"
                                                max={baseConfig.maximumBillUnits ?? ''}
                                                required={!!baseConfig.minimumBillUnits}
                                                error="Min bill must be less than or equal to max bill"
                                            />
                                        )}
                                        <NumericInput
                                            label="Maximum Bill"
                                            ref={this.maxPayRef}
                                            id="chargeType.baseConfig.maximumBillUnits"
                                            name="chargeType.baseConfig.maximumBillUnits"
                                            value={baseConfig.maximumBillUnits ?? ''}
                                            onChange={this.handlers.change}
                                            placeholder={`Enter  ${unitsName}`}
                                            step="0.25"
                                            required={!!baseConfig.maximumBillUnits}
                                            error="Max bill must be greater than or equal to min bill"
                                        />

                                        {(tenantSettings ?? {})
                                            .useStateMinimum && (
                                                <FormGroup>
                                                    <FormLabel label="State Minimum" />
                                                    <FormCheckbox
                                                        id="chargeType.baseConfig.isStateMinimum"
                                                        name="chargeType.baseConfig.isStateMinimum"
                                                        checked={
                                                            baseConfig.isStateMinimum
                                                            ?? ''
                                                        }
                                                        onChange={
                                                            this.handlers.change
                                                        }
                                                        labelText="Use State Minimum Pay Category"
                                                    />
                                                </FormGroup>
                                            )}
                                        <FormGroup>
                                            <FormLabel label="Timesheet Options" />
                                            <FormCheckbox
                                                id="chargeType.baseConfig.disallowNA"
                                                name="chargeType.baseConfig.disallowNA"
                                                checked={
                                                    baseConfig.disallowNA
                                                    ?? ''
                                                }
                                                onChange={
                                                    this.handlers.change
                                                }
                                                labelText="Require Data Entry on Timesheet (Do not allow N/A reply)"
                                            />
                                        </FormGroup>

                                        {tenantSettings.timesheetEquipmentAuditEnabled && chargeType.resourceTypeId === ChargeTypeResourceTypes.Employee && (
                                            <>
                                                <FormGroup>
                                                    <FormCheckbox
                                                        id="chargeType.baseConfig.trackStartTime"
                                                        name="chargeType.baseConfig.trackStartTime"
                                                        checked={baseConfig.trackStartTime ?? ''}
                                                        onChange={this.handlers.change}
                                                        labelText="Track Start Time"
                                                    />
                                                </FormGroup>

                                                {Boolean(baseConfig.trackStartTime) && (
                                                    <ValidatedSelect
                                                        id="chargeType.baseConfig.chargeTypeTrackingTypeId"
                                                        name="chargeType.baseConfig.chargeTypeTrackingTypeId"
                                                        label="What type of trackable charge is this?"
                                                        required
                                                        isClearable
                                                        options={chargeTypeTrackingTypes}
                                                        value={
                                                            (chargeTypeTrackingTypes ?? []).find(
                                                                (x) => x.value === baseConfig.chargeTypeTrackingTypeId,
                                                            ) ?? ''
                                                        }
                                                        onChange={this.handlers.select}
                                                        error="A tracking type is required"
                                                    />
                                                )}
                                            </>

                                        )}

                                        <FormGroup>
                                            <FormLabel label="Invoicing" />

                                            <FormCheckbox
                                                id="chargeType.baseConfig.omitFromInvoice"
                                                name="chargeType.baseConfig.omitFromInvoice"
                                                checked={
                                                    baseConfig.omitFromInvoice
                                                    ?? ''
                                                }
                                                onChange={
                                                    this.handlers.change
                                                }
                                                labelText="Omit From Charge Details On Invoice"
                                            />

                                            <FormCheckbox
                                                id="chargeType.baseConfig.excludeFromInvoiceGrouping"
                                                name="chargeType.baseConfig.excludeFromInvoiceGrouping"
                                                checked={
                                                    baseConfig.excludeFromInvoiceGrouping
                                                    ?? ''
                                                }
                                                onChange={
                                                    this.handlers.change
                                                }
                                                labelText="Do Not Group This in Charge Details On Invoice"
                                            />
                                        </FormGroup>

                                        {chargeType.resourceTypeId === ChargeTypeResourceTypes.Employee && chargeType.isPayable == true && (
                                            <FormGroup>
                                                <FormLabel text="Payroll" />
                                                <FormCheckbox
                                                    id="chargeType.baseConfig.excludeFromOvertimeCalculation"
                                                    name="chargeType.baseConfig.excludeFromOvertimeCalculation"
                                                    checked={
                                                        baseConfig.excludeFromOvertimeCalculation
                                                        ?? ''
                                                    }
                                                    onChange={
                                                        this.handlers.change
                                                    }
                                                    labelText="Exclude From Payroll OT and DT Calculation"
                                                />
                                                <FormCheckbox
                                                    id="chargeType.baseConfig.doNotApplyIncentives"
                                                    name="chargeType.baseConfig.doNotApplyIncentives"
                                                    checked={
                                                        baseConfig.doNotApplyIncentives
                                                        ?? ''
                                                    }
                                                    onChange={
                                                        this.handlers.change
                                                    }
                                                    labelText="Do Not Apply Payroll Incentives"
                                                />
                                            </FormGroup>
                                        )}

                                        {hasHourlyEquipment && (
                                            <FormGroup>
                                                <FormLabel label="Hourly Equipment" />
                                                <FormCheckbox
                                                    id="chargeType.baseConfig.hourlyEquipmentOvertime"
                                                    name="chargeType.baseConfig.hourlyEquipmentOvertime"
                                                    checked={
                                                        baseConfig.hourlyEquipmentOvertime ?? false
                                                    }
                                                    onChange={this.handlers.change}
                                                    labelText="Overtime applies to hourly equipment charge"
                                                />
                                            </FormGroup>
                                        )}

                                        {chargeType.unitsId === ChargeTypeUnits.Hours && (
                                            <>
                                                <FormLabel
                                                    id="conditionalSurchargeLabel"
                                                    label="Conditional Billing Surcharge"
                                                    helpMessage="
                                                        If selected, a surcharge will be added to the timesheet
                                                        if the hours for this charge exceeds a threshold.
                                                        The quantity billed for the surcharge will equal the difference
                                                        between the charge amount and the threshold.
                                                    "
                                                />

                                                <Block className="mb-2">
                                                    <NumericInput
                                                        label="If the Hours for this Charge Exceeds"
                                                        id="chargeType.baseConfig.conditionalChargeTypeHoursThreshold"
                                                        name="chargeType.baseConfig.conditionalChargeTypeHoursThreshold"
                                                        value={baseConfig.conditionalChargeTypeHoursThreshold ?? ''}
                                                        onChange={this.handlers.change}
                                                        placeholder="Enter a number of hours"
                                                        step={0.01}
                                                        min={0}
                                                    />

                                                    <ValidatedSelect
                                                        id="chargeType.baseConfig.conditionalChargeTypeId"
                                                        name="chargeType.baseConfig.conditionalChargeTypeId"
                                                        label="Add the Difference as This Surcharge"
                                                        isClearable
                                                        options={surcharges}
                                                        value={
                                                            (surcharges ?? []).find(
                                                                (x) => x.value === baseConfig.conditionalChargeTypeId,
                                                            ) ?? ''
                                                        }
                                                        onChange={this.handlers.select}
                                                    />
                                                </Block>
                                            </>
                                        )}
                                    </FormGroupColumn>
                                ) : (
                                    <FormGroupColumn>
                                        <FormGroup>
                                            <FormLabel label="Invoicing" />
                                            <FormCheckbox
                                                id="chargeType.baseConfig.omitFromInvoice"
                                                name="chargeType.baseConfig.omitFromInvoice"
                                                checked={
                                                    baseConfig.omitFromInvoice
                                                    ?? ''
                                                }
                                                onChange={
                                                    this.handlers.change
                                                }
                                                labelText="Omit From Charge Details On Invoice"
                                            />

                                            <FormCheckbox
                                                id="chargeType.baseConfig.excludeFromInvoiceGrouping"
                                                name="chargeType.baseConfig.excludeFromInvoiceGrouping"
                                                checked={
                                                    baseConfig.excludeFromInvoiceGrouping
                                                    ?? ''
                                                }
                                                onChange={
                                                    this.handlers.change
                                                }
                                                labelText="Do Not Group This in Charge Details On Invoice"
                                            />
                                        </FormGroup>

                                        {chargeType.resourceTypeId === ChargeTypeResourceTypes.Employee && chargeType.isPayable == true && (
                                            <FormGroup>
                                                <FormLabel text="Payroll" />
                                                <FormCheckbox
                                                    id="chargeType.baseConfig.excludeFromOvertimeCalculation"
                                                    name="chargeType.baseConfig.excludeFromOvertimeCalculation"
                                                    checked={
                                                        baseConfig.excludeFromOvertimeCalculation
                                                        ?? ''
                                                    }
                                                    onChange={
                                                        this.handlers.change
                                                    }
                                                    labelText="Exclude From Payroll OT and DT Calculation"
                                                />
                                                <FormCheckbox
                                                    id="chargeType.baseConfig.doNotApplyIncentives"
                                                    name="chargeType.baseConfig.doNotApplyIncentives"
                                                    checked={
                                                        baseConfig.doNotApplyIncentives
                                                        ?? ''
                                                    }
                                                    onChange={
                                                        this.handlers.change
                                                    }
                                                    labelText="Do Not Apply Payroll Incentives"
                                                />
                                            </FormGroup>
                                        )}
                                    </FormGroupColumn>
                                )}
                            </>
                        )}

                        {chargeType.unitsId === ChargeTypeUnits.Flat && (
                            <>
                                {chargeType.resourceTypeId !== ChargeTypeResourceTypes.Sale ? (
                                    <FormGroupColumn>
                                        <FormGroup>
                                            <FormLabel label="Surcharge Options" />
                                            <FlexRow className="ml-3">
                                                <FormCheckbox
                                                    id="chargeType.baseConfig.isSurcharge"
                                                    name="chargeType.baseConfig.isSurcharge"
                                                    checked={
                                                        baseConfig.isSurcharge ?? ''
                                                    }
                                                    onChange={this.handlers.change}
                                                    labelText="Surcharge Charge Type"
                                                />
                                            </FlexRow>
                                        </FormGroup>
                                        {baseConfig.isSurcharge ? (
                                            <>
                                                <NumericInput
                                                    formGroupClassName="ml-3"
                                                    label="Bill Value"
                                                    ref={this.minPayRef}
                                                    id="chargeType.baseConfig.surchargeBillAmount"
                                                    name="chargeType.baseConfig.surchargeBillAmount"
                                                    value={baseConfig.surchargeBillAmount ?? ''}
                                                    onChange={this.handlers.change}
                                                    placeholder={`Enter  ${unitsName}`}
                                                    required={!!baseConfig.surchargeBillAmount >= 0}
                                                    error="Bill Value is required."
                                                />

                                                <NumericInput
                                                    formGroupClassName="ml-3"
                                                    label="Pay Value"
                                                    ref={this.minPayRef}
                                                    id="chargeType.baseConfig.surchargePayAmount"
                                                    name="chargeType.baseConfig.surchargePayAmount"
                                                    value={baseConfig.surchargePayAmount ?? ''}
                                                    onChange={this.handlers.change}
                                                    placeholder={`Enter  ${unitsName}`}
                                                    required={!!baseConfig.surchargePayAmount >= 0}
                                                    error="Pay Value is required."
                                                />
                                            </>
                                        ) : (
                                            <NumericInput
                                                label="Increment"
                                                id="chargeType.baseConfig.increment"
                                                name="chargeType.baseConfig.increment"
                                                value={baseConfig.increment ?? ''}
                                                onChange={this.handlers.change}
                                                placeholder={`Enter  ${unitsName}`}
                                                step="0.01"
                                                required={!!baseConfig.increment}
                                                error="Min pay must be less than or equal to max pay."
                                            />
                                        )}

                                        {allowDefaultChargeTypeValues && (
                                            <NumericInput
                                                label="Default Value"
                                                name="chargeType.baseConfig.defaultValue"
                                                value={baseConfig.defaultValue ?? ''}
                                                onChange={this.handlers.change}
                                                placeholder={`Enter ${unitsName}`}
                                                step="0.25"
                                                required
                                            />
                                        )}

                                        <FormGroup>
                                            <FormLabel label="Cancellation Options" />
                                            {(tenantSettings ?? {})
                                                .blockBillingCancelEnabled && (
                                                    <FormGroup>
                                                        <FlexRow className="ml-3">
                                                            <FormCheckbox
                                                                id="chargeType.baseConfig.blockBillingCancel"
                                                                name="chargeType.baseConfig.blockBillingCancel"
                                                                checked={
                                                                    baseConfig.blockBillingCancel
                                                                    ?? ''
                                                                }
                                                                onChange={
                                                                    this.handlers.change
                                                                }
                                                                labelText="Block Cancellation Min Bill Qty if Charge > 0 for Billing"
                                                            />
                                                        </FlexRow>
                                                    </FormGroup>
                                                )}
                                            {(tenantSettings ?? {})
                                                .blockPayrollCancelEnabled && (
                                                    <FormGroup>
                                                        <FlexRow className="ml-3">
                                                            <FormCheckbox
                                                                id="chargeType.baseConfig.blockPayrollCancel"
                                                                name="chargeType.baseConfig.blockPayrollCancel"
                                                                checked={
                                                                    baseConfig.blockPayrollCancel
                                                                    ?? ''
                                                                }
                                                                onChange={
                                                                    this.handlers.change
                                                                }
                                                                labelText="Block Cancellation Min Pay Qty if Charge > 0 for Payroll"
                                                            />
                                                        </FlexRow>
                                                    </FormGroup>
                                                )}
                                        </FormGroup>
                                        <NumericInput
                                            formGroupClassName="ml-3"
                                            label="Cancellation Time Threshold (Min)"
                                            id="chargeType.baseConfig.cancellationTimeThreshold"
                                            name="chargeType.baseConfig.cancellationTimeThreshold"
                                            value={baseConfig.cancellationTimeThreshold ?? ''}
                                            onChange={this.handlers.change}
                                            placeholder="Enter in Minutes"
                                            required={!!baseConfig.canBreak}
                                        />
                                        <NumericInput
                                            formGroupClassName="ml-3"
                                            label="Minimum Cancellation Bill Qty (Flat)"
                                            id="chargeType.baseConfig.cancellationBillQty"
                                            name="chargeType.baseConfig.cancellationBillQty"
                                            value={baseConfig.cancellationBillQty ?? ''}
                                            onChange={this.handlers.change}
                                            placeholder="Enter Qty Flat"
                                            required={!!baseConfig.canBreak}
                                            step=".025"
                                        />
                                    </FormGroupColumn>
                                ) : (
                                    <FormGroupColumn>
                                        <NumericInput
                                            label="Increment"
                                            id="chargeType.baseConfig.increment"
                                            name="chargeType.baseConfig.increment"
                                            value={baseConfig.increment ?? ''}
                                            onChange={this.handlers.change}
                                            placeholder={`Enter  ${unitsName}`}
                                            step="0.01"
                                            required={!!baseConfig.increment}
                                            error="Min pay must be less than or equal to max pay."
                                        />
                                    </FormGroupColumn>

                                )}

                                {!baseConfig.isSurcharge && chargeType.resourceTypeId !== ChargeTypeResourceTypes.Sale ? (
                                    <FormGroupColumn>
                                        <NumericInput
                                            ref={this.minPayRef}
                                            label="Minimum Bill"
                                            id="chargeType.baseConfig.minimumBillUnits"
                                            name="chargeType.baseConfig.minimumBillUnits"
                                            value={baseConfig.minimumBillUnits ?? ''}
                                            onChange={this.handlers.change}
                                            placeholder="Enter in Minutes"
                                            required={!!baseConfig.minimumBillUnits}
                                            step="0.25"
                                            max={baseConfig.maximumBillUnits ?? ''}
                                            error="Min bill must be less than or equal to max pay."
                                        />
                                        <NumericInput
                                            ref={this.maxPayRef}
                                            label="Maximum Bill"
                                            id="chargeType.baseConfig.maximumBillUnits"
                                            name="chargeType.baseConfig.maximumBillUnits"
                                            value={baseConfig.maximumBillUnits ?? ''}
                                            onChange={this.handlers.change}
                                            placeholder="Enter Qty Flat"
                                            required={!!baseConfig.canBreak}
                                            step="0.25"
                                            error="Max bill must be greater than or equal to min pay."
                                        />

                                        {(tenantSettings ?? {})
                                            .useStateMinimum && (
                                                <FormGroup>
                                                    <FormLabel label="State Minimum" />
                                                    <FormCheckbox
                                                        id="chargeType.baseConfig.isStateMinimum"
                                                        name="chargeType.baseConfig.isStateMinimum"
                                                        checked={
                                                            baseConfig.isStateMinimum
                                                            ?? ''
                                                        }
                                                        onChange={
                                                            this.handlers.change
                                                        }
                                                        labelText="Use State Minimum"
                                                    />
                                                </FormGroup>
                                            )}
                                        <FormGroup>
                                            <FormLabel label="Timesheet Options" />
                                            <FormCheckbox
                                                id="chargeType.baseConfig.disallowNA"
                                                name="chargeType.baseConfig.disallowNA"
                                                checked={
                                                    baseConfig.disallowNA
                                                    ?? ''
                                                }
                                                onChange={
                                                    this.handlers.change
                                                }
                                                labelText="Require Data Entry on Timesheet (Do not allow N/A reply)"
                                            />
                                        </FormGroup>

                                        {Boolean(hasSpecificEquipmentOnly) && chargeType.resourceTypeId === ChargeTypeResourceTypes.Rental && (
                                            <ValidatedSelect
                                                id="chargeType.baseConfig.longTermStatusId"
                                                name="chargeType.baseConfig.longTermStatusId"
                                                label="Equipment On Site Should Have This Status"
                                                isClearable
                                                options={equipmentLongTermStatuses}
                                                value={
                                                    (equipmentLongTermStatuses ?? []).find(
                                                        (x) => x.value === baseConfig.longTermStatusId,
                                                    ) ?? ''
                                                }
                                                onChange={this.handlers.select}
                                                required
                                                error="Rental equipment must be given a long-term status"
                                            />
                                        )}

                                        <FormGroup>
                                            <FormLabel label="Invoicing" />
                                            <FormCheckbox
                                                id="chargeType.baseConfig.omitFromInvoice"
                                                name="chargeType.baseConfig.omitFromInvoice"
                                                checked={
                                                    baseConfig.omitFromInvoice
                                                    ?? ''
                                                }
                                                onChange={
                                                    this.handlers.change
                                                }
                                                labelText="Omit From Charge Details On Invoice"
                                            />

                                            <FormCheckbox
                                                id="chargeType.baseConfig.excludeFromInvoiceGrouping"
                                                name="chargeType.baseConfig.excludeFromInvoiceGrouping"
                                                checked={
                                                    baseConfig.excludeFromInvoiceGrouping
                                                    ?? ''
                                                }
                                                onChange={
                                                    this.handlers.change
                                                }
                                                labelText="Do Not Group This in Charge Details On Invoice"
                                            />
                                        </FormGroup>
                                    </FormGroupColumn>
                                ) : (
                                    <FormGroupColumn>
                                        <FormGroup>
                                            <FormLabel label="Invoicing" />
                                            <FormCheckbox
                                                id="chargeType.baseConfig.omitFromInvoice"
                                                name="chargeType.baseConfig.omitFromInvoice"
                                                checked={
                                                    baseConfig.omitFromInvoice
                                                    ?? ''
                                                }
                                                onChange={
                                                    this.handlers.change
                                                }
                                                labelText="Omit From Charge Details On Invoice"
                                            />

                                            <FormCheckbox
                                                id="chargeType.baseConfig.excludeFromInvoiceGrouping"
                                                name="chargeType.baseConfig.excludeFromInvoiceGrouping"
                                                checked={
                                                    baseConfig.excludeFromInvoiceGrouping
                                                    ?? ''
                                                }
                                                onChange={
                                                    this.handlers.change
                                                }
                                                labelText="Do Not Group This in Charge Details On Invoice"
                                            />
                                            <FormCheckbox
                                                id="chargeType.baseConfig.isUploadsAllowed"
                                                name="chargeType.baseConfig.isUploadsAllowed"
                                                checked={
                                                    baseConfig.isUploadsAllowed
                                                    ?? ''
                                                }
                                                onChange={
                                                    this.handlers.change
                                                }
                                                labelText="Allow File Attachments"
                                            />
                                        </FormGroup>
                                    </FormGroupColumn>
                                )}
                            </>
                        )}
                    </GroupedRow>

                    {
                        (chargeType.resourceTypeId === ChargeTypeResourceTypes.Employee || chargeType.resourceTypeId === ChargeTypeResourceTypes.Equipment)
                        && tenantSettings.enforceContractUnionWork === true
                        && (
                            <>
                                <SubHeading>Union Settings</SubHeading>
                                <GroupedRow>
                                    <FormGroupColumn>
                                        {chargeType.resourceTypeId === ChargeTypeResourceTypes.Employee && chargeType.isPayable == true && (
                                            <>
                                                <ValidatedSelect
                                                    id="chargeType.payrollChargeTypeId"
                                                    name="chargeType.payrollChargeTypeId"
                                                    label="Payroll Charge Category"
                                                    options={payrollChargeTypes}
                                                    required
                                                    value={
                                                        (payrollChargeTypes ?? []).find(
                                                            (x) => x.value === (chargeType.payrollChargeTypeId ?? -1),
                                                        ) ?? ''
                                                    }
                                                    onChange={this.handlers.select}
                                                    isClearable
                                                    error="A category must be selected"
                                                />

                                                <Input
                                                    id="chargeType.payrollCode"
                                                    name="chargeType.payrollCode"
                                                    label="Pay Code Override"
                                                    value={chargeType.payrollCode ?? ''}
                                                    onChange={this.handlers.change}
                                                    placeholder="e.g. Shop, Per Diem, Bereavement, etc."
                                                    type="text"
                                                />
                                            </>
                                        )}

                                        {chargeType.unitsId === ChargeTypeUnits.Hours && (
                                            <FormGroup>
                                                <FormCheckbox
                                                    id="chargeType.isSubjectToBumpRules"
                                                    name="chargeType.isSubjectToBumpRules"
                                                    checked={
                                                        chargeType.isSubjectToBumpRules ?? ''
                                                    }
                                                    onChange={this.handlers.change}
                                                    labelText="This charge type is subject to bump rules"
                                                />
                                            </FormGroup>
                                        )}
                                    </FormGroupColumn>

                                    {chargeType.resourceTypeId === ChargeTypeResourceTypes.Employee && chargeType.isPayable == true && (
                                        <FormGroupColumn>
                                            <>
                                                <ValidatedSelect
                                                    id="chargeType.chargeTypePaySourceId"
                                                    name="chargeType.chargeTypePaySourceId"
                                                    label="Pay Rate Source"
                                                    required
                                                    options={paySources}
                                                    value={
                                                        paySources.find(
                                                            (bo) => bo.value === chargeType.chargeTypePaySourceId,
                                                        ) ?? ''
                                                    }
                                                    onChange={this.handlers.select}
                                                    error="The pay rate source is required"
                                                />
                                                <NumericInput
                                                    label={`Minimum Cancellation Pay Qty ${chargeType.unitsId == ChargeTypeUnits.Hours ? '(Hours)' : '(Flat)'
                                                        }`}
                                                    id="chargeType.cancellationPayQty"
                                                    name="chargeType.cancellationPayQty"
                                                    value={chargeType.cancellationPayQty ?? ''}
                                                    onChange={this.handlers.change}
                                                    placeholder="Enter Qty"
                                                    step="0.25"
                                                />
                                            </>
                                        </FormGroupColumn>
                                    )}
                                </GroupedRow>
                            </>
                        )}

                    {chargeType.id && (
                        <>
                            <SubHeading>Exceptions</SubHeading>
                            <GroupedRow>
                                <ChargeTypeConfigurationInfo
                                    chargeUnitId={chargeType.unitsId}
                                    increments={increments}
                                    incrementRequired
                                    customers={customers}
                                    exceptions={chargeType.exceptions}
                                    onAddException={this.onAddException}
                                    onEditException={this.onEditException}
                                />
                            </GroupedRow>
                        </>
                    )}

                    {chargeType.id
                        && chargeType.isBillable
                        && billingEnabled
                        && billingRatesEnabled && (
                            <>
                                <SubHeading>Billing Rates</SubHeading>
                                <GroupedRow>
                                    <BillingRateGrid
                                        rates={orderBy(
                                            chargeType.billingRates,
                                            (x) => [
                                                x.chargeTypeName,
                                                x.billingRate.effectiveDate,
                                            ],
                                        )}
                                        showDoubleTime
                                        showShiftDifferential
                                        onAddRate={this.onAddRate}
                                        onEditRate={this.onEditRate}
                                        onShowRateHistory={
                                            this.onShowRateHistory
                                        }
                                    />
                                </GroupedRow>
                            </>
                        )}
                    <FlexCenterRow className="mb-3">
                        <Button
                            size="sm"
                            type="submit"
                            color="primary"
                            name="chargeTypeForm"
                        >
                            <FontAwesomeIcon
                                className="mr-1"
                                icon={faSave}
                            />
                            {newChargeType ? 'Save New Charge Type' : 'Save'}
                        </Button>
                    </FlexCenterRow>
                </AppPageForm>
                <ChargeTypeConfigurationForm
                    ref={this.exceptionFormRef}
                    id="chargeTypeConfigForm"
                    surcharges={surcharges}
                    requireIncrement
                    entity={this.state.selectedException}
                    entityName="selectedException"
                    customers={this.state.customers}
                    existingExceptions={chargeType.exceptions}
                    increments={increments}
                    chargeUnitId={chargeType.unitsId}
                    show={this.state.showExceptionForm}
                    onClose={this.onExceptionFormClosed}
                    onSaveCallback={this.onExceptionSaved}
                    onRemoveCallback={this.onExceptionRemoved}
                    onChange={this.handlers.change}
                    onSelectChange={this.handlers.select}
                    onCanBreakChanged={this.handlers.selectedException.canBreak}
                    onDoesNotApplyChanged={this.handlers.selectedException.DNA}
                    tenantSettings={tenantSettings}
                    hasHourlyEquipment={hasHourlyEquipment}
                    isNewException={isNewException}
                    isBillable={chargeType.isBillable}
                />

                {billingEnabled && billingRatesEnabled && (
                    <>
                        <BillingRateForm
                            ref={this.rateFormRef}
                            id="billingRateForm"
                            entity={this.state.selectedRate}
                            entityName="selectedRate"
                            show={this.state.showRateForm}
                            chargeTypes={[chargeType]}
                            onClose={this.onRateFormClosed}
                            onSaveCallback={this.onRateSaved}
                            onRemoveCallback={this.onRateRemoved}
                            onChange={this.handlers.change}
                            onSelectChange={this.handlers.select}
                            showRemove={showRateFormDelete}
                        />
                        <BillingRateHistoryForm
                            ref={this.rateHistoryFormRef}
                            id="billingRateHistoryForm"
                            entity={this.state.selectedRateHistory}
                            show={this.state.showRateHistoryForm}
                            onClose={this.onRateHistoryFormClosed}
                        />
                    </>
                )}
            </>
        );
    }

    render() {
        if (this.state.loading) return <Progress />;
        return this.renderForm();
    }
}
export default ChargeTypeForm;
